import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildingChances,
  containerShares,
  explain,
  itemChanceAt,
  namesOf,
  prettyName,
  REJECTION_TEXT,
  tierLabel,
  type Building,
  type Item,
  type LootModel,
} from '../lib/loot';
import { gridRef } from '../lib/maps';
import { readEconomyRows, type EditorDoc } from '../lib/model';
import { useDispatch } from '../state/store';
import { TagEditor } from './EconomyPanel';
import { containerColor, tierColor } from './MapCanvas';

const PAGE = 60;

const pct = (value: number) =>
  value >= 0.1 ? `${(value * 100).toFixed(1)}%` : value >= 0.001 ? `${(value * 100).toFixed(2)}%` : '<0.1%';

type Props = {
  model: LootModel;
  typesDoc: EditorDoc;
  building: Building | null;
  mapSize: number;
  highlightItem: string | null;
  onHighlight: (name: string | null) => void;
  onSelectBuilding: (index: number | null) => void;
  onZoomToBuilding: (index: number) => void;
};

/**
 * What can spawn in the selected loot building, and how likely each item is,
 * with the types.xml values that drive it editable in place.
 */
export function LootPanel(props: Props) {
  return (
    <div className="panel loot-panel">
      <ItemFinder {...props} />
      {props.building ? (
        <BuildingLoot {...props} building={props.building} />
      ) : (
        <p className="empty">
          Zoom in and click a building on the map to see what can spawn in it. Coloured rings mark loot buildings by
          tier; zoom further to see every loot spot.
        </p>
      )}
    </div>
  );
}

function ItemFinder({ model, highlightItem, onHighlight, onZoomToBuilding, onSelectBuilding }: Props) {
  const [text, setText] = useState(highlightItem ?? '');
  // "Show on map" in the building list picks an item from outside this input.
  useEffect(() => {
    if (highlightItem) setText(highlightItem);
  }, [highlightItem]);
  const item = highlightItem ? model.byName.get(highlightItem) : undefined;

  const summary = useMemo(() => {
    if (!item) return null;
    const hits = model.data.buildings
      .map((b) => ({ b, chance: itemChanceAt(model, b, item) }))
      .filter((h) => h.chance > 0)
      .sort((a, b) => b.chance - a.chance);
    return { hits, slots: model.slots.get(item.signature) ?? 0 };
  }, [item, model]);

  const submit = (value: string) => {
    setText(value);
    const match = model.byName.get(value.trim()) ?? model.items.find((i) => i.name.toLowerCase() === value.trim().toLowerCase());
    onHighlight(match ? match.name : null);
  };

  return (
    <div className="finder">
      <h2>Where does it spawn?</h2>
      <div className="finder-row">
        <input
          list="loot-item-names"
          placeholder="Item class, e.g. AKM"
          value={text}
          onChange={(e) => submit(e.target.value)}
        />
        {highlightItem && (
          <button type="button" onClick={() => submit('')} title="Stop highlighting">
            ✕
          </button>
        )}
      </div>
      <datalist id="loot-item-names">
        {model.items.map((i) => (
          <option key={i.rowId} value={i.name} />
        ))}
      </datalist>
      {item && summary && (
        <div className="finder-result">
          {summary.hits.length === 0 ? (
            <p className="warn-text">
              {item.name} never spawns in buildings:{' '}
              {item.nominal <= 0
                ? REJECTION_TEXT.nominal
                : 'no loot building matches its usage / tier / category / tag combination'}
              . It may still come from events, infected or other sources.
            </p>
          ) : (
            <>
              <p className="muted">
                Can spawn in <b>{summary.hits.length}</b> buildings ({summary.slots} loot spots) · nominal {item.nominal} ·
                brighter = more likely. Best spots:
              </p>
              <ul className="finder-list">
                {summary.hits.slice(0, 6).map(({ b, chance }) => (
                  <li key={b.index}>
                    <button
                      type="button"
                      className="link"
                      onClick={() => {
                        onSelectBuilding(b.index);
                        onZoomToBuilding(b.index);
                      }}
                    >
                      {prettyName(model.data.protos[b.proto].name)}
                    </button>
                    <span className="muted">{pct(chance)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BuildingLoot({
  model,
  typesDoc,
  building,
  mapSize,
  onHighlight,
  onSelectBuilding,
  onZoomToBuilding,
}: Props & { building: Building }) {
  const dispatch = useDispatch();
  const [container, setContainer] = useState<number | 'all'>('all');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [why, setWhy] = useState('');

  const panelRef = useRef<HTMLDivElement>(null);
  // Bring a newly picked building into view (below the finder, or inside a phone's bottom sheet).
  useEffect(() => {
    panelRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [building.index]);

  const { data } = model;
  const proto = data.protos[building.proto];
  const shares = containerShares(proto);
  const chances = useMemo(() => buildingChances(model, building), [model, building]);
  const rows = useMemo(() => new Map(readEconomyRows(typesDoc).map((r) => [r.id, r])), [typesDoc]);

  const list = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return chances
      .map((c) => ({ ...c, shown: container === 'all' ? c.chance : c.byContainer[container] }))
      .filter((c) => !Number.isNaN(c.shown) && c.shown > 0)
      .filter((c) => !needle || c.item.name.toLowerCase().includes(needle))
      .sort((a, b) => b.shown - a.shown);
  }, [chances, container, query]);

  const whyItem = model.byName.get(why.trim());
  const whyReason = whyItem ? explain(model, building, whyItem) : null;
  const totalPoints = proto.containers.reduce((sum, c) => sum + c.points.length, 0);

  const setField = (item: Item, field: string, value: string) =>
    dispatch({ type: 'economySetField', docId: typesDoc.id, rowId: item.rowId, field, value });

  return (
    <div className="building" ref={panelRef}>
      <div className="building-head">
        <div>
          <b>{prettyName(proto.name)}</b>
          <div className="muted mono">{proto.name}</div>
        </div>
        <button type="button" className="icon" title="Close" onClick={() => onSelectBuilding(null)}>
          ✕
        </button>
      </div>

      <div className="building-meta">
        <span>
          <span className="dot" style={{ background: tierColor(building.valueMask) }} />
          {tierLabel(data, building.valueMask)}
          {proto.value.length > 0 && <span className="muted"> (set by prototype)</span>}
        </span>
        <span>
          usage <b>{namesOf(data.usageFlags, building.usageMask).join(', ') || 'none'}</b>
          {proto.usage.length === 0 && <span className="muted"> (from area)</span>}
        </span>
        <span className="muted">
          x {building.x.toFixed(1)} · z {building.z.toFixed(1)} · grid {gridRef(building.x, building.z, mapSize)}
        </span>
        <span className="muted">
          up to {proto.lootmax} items at once · {totalPoints} loot spots
        </span>
        <button type="button" onClick={() => onZoomToBuilding(building.index)}>
          Zoom to building
        </button>
      </div>

      <div className="container-tabs">
        <button type="button" className={container === 'all' ? 'active' : ''} onClick={() => setContainer('all')}>
          All
        </button>
        {proto.containers.map((c, i) => (
          <button
            key={c.name + i}
            type="button"
            className={container === i ? 'active' : ''}
            onClick={() => setContainer(i)}
            title={`${c.categories.join(', ') || 'any category'}${c.tags.length ? ` · tags ${c.tags.join(', ')}` : ''} · lootmax ${c.lootmax} · ${Math.round(shares[i] * 100)}% of this building's loot`}
          >
            <span className="dot" style={{ background: containerColor(c.name) }} />
            {c.name.replace(/^loot/i, '') || c.name} {c.points.length}
          </button>
        ))}
      </div>
      {container !== 'all' && (
        <p className="hint">
          Accepts {proto.containers[container].categories.join(', ') || 'any category'}
          {proto.containers[container].tags.length > 0 && <> · tags {proto.containers[container].tags.join(', ')}</>} ·
          lootmax {proto.containers[container].lootmax}
        </p>
      )}
      {proto.dispatch.length > 0 && (
        <p className="hint">Always carries: {[...new Set(proto.dispatch)].join(', ')}</p>
      )}

      <input
        type="search"
        className="wide"
        placeholder={`Filter ${list.length} items…`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <table className="loot-table">
        <thead>
          <tr>
            <th>item</th>
            <th title="Chance that an item spawning here is this one">chance</th>
            <th title="types.xml nominal - how many exist map-wide">nominal</th>
          </tr>
        </thead>
        <tbody>
          {list.slice(0, limit).map(({ item, shown }) => {
            const row = rows.get(item.rowId);
            return (
              <Fragment key={item.rowId}>
                <tr>
                  <td className="loot-name">
                    <button
                      type="button"
                      className="link"
                      title="Edit usage / tier / category tags"
                      onClick={() => setExpanded(expanded === item.rowId ? null : item.rowId)}
                    >
                      {item.name}
                    </button>
                    <span className="muted small"> {item.value.map((v) => v.replace('Tier', 'T')).join(' ')}</span>
                  </td>
                  <td>
                    <span className="bar" style={{ width: `${Math.min(100, (shown / (list[0]?.shown || 1)) * 100)}%` }} />
                    {pct(shown)}
                  </td>
                  <td>
                    <input
                      className="cell small-cell"
                      type="number"
                      min={0}
                      value={row?.fields.nominal ?? ''}
                      onChange={(e) => setField(item, 'nominal', e.target.value)}
                    />
                  </td>
                </tr>
                {expanded === item.rowId && row && (
                  <tr className="tag-editor-row">
                    <td colSpan={3}>
                      <div className="expanded-item">
                        <label>
                          min
                          <input
                            className="cell small-cell"
                            type="number"
                            min={0}
                            value={row.fields.min ?? ''}
                            onChange={(e) => setField(item, 'min', e.target.value)}
                          />
                        </label>
                        <button type="button" onClick={() => onHighlight(item.name)}>
                          Show on map
                        </button>
                      </div>
                      <TagEditor docId={typesDoc.id} rowId={row.id} tags={row.tags} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {list.length > limit && (
        <button type="button" className="wide" onClick={() => setLimit(limit + PAGE)}>
          Show more ({list.length - limit} left)
        </button>
      )}

      <div className="why">
        <h2>Why can't it spawn here?</h2>
        <input list="loot-item-names" placeholder="Item class" value={why} onChange={(e) => setWhy(e.target.value)} />
        {whyItem && (
          <div className="why-result">
            {whyReason === null ? (
              <p className="ok">
                {whyItem.name} can spawn here: {pct(itemChanceAt(model, building, whyItem))} of this building's loot.
              </p>
            ) : (
              <>
                <p className="warn-text">
                  {whyItem.name} cannot spawn here - {REJECTION_TEXT[whyReason]}.
                </p>
                <WhyFix model={model} building={building} item={whyItem} reason={whyReason} typesDoc={typesDoc} />
              </>
            )}
          </div>
        )}
      </div>

      <p className="hint">
        Chances are estimates from types.xml: each item's nominal is spread over every loot spot on the map that
        accepts it, then compared with everything else that fits the same spot. Edits apply instantly - export
        types.xml when you are done.
      </p>
    </div>
  );
}

/** One-click fixes for the common "why not" answers. */
function WhyFix({
  model,
  building,
  item,
  reason,
  typesDoc,
}: {
  model: LootModel;
  building: Building;
  item: Item;
  reason: string;
  typesDoc: EditorDoc;
}) {
  const dispatch = useDispatch();
  const toggle = (tag: 'usage' | 'value', name: string) =>
    dispatch({ type: 'economyToggleTag', docId: typesDoc.id, rowId: item.rowId, tag, name });

  if (reason === 'nominal') {
    return (
      <button
        type="button"
        onClick={() => dispatch({ type: 'economySetField', docId: typesDoc.id, rowId: item.rowId, field: 'nominal', value: '5' })}
      >
        Set nominal to 5
      </button>
    );
  }
  if (reason === 'usage') {
    return (
      <div className="quick-row">
        {namesOf(model.data.usageFlags, building.usageMask).map((usage) => (
          <button key={usage} type="button" onClick={() => toggle('usage', usage)}>
            Add usage {usage}
          </button>
        ))}
      </div>
    );
  }
  if (reason === 'tier') {
    return (
      <div className="quick-row">
        {namesOf(model.data.valueFlags, building.valueMask).map((value) => (
          <button key={value} type="button" onClick={() => toggle('value', value)}>
            Add {value}
          </button>
        ))}
      </div>
    );
  }
  return (
    <p className="hint">
      The building's containers take {[...new Set(model.data.protos[building.proto].containers.flatMap((c) => c.categories))].join(', ')}
      . Change the item's category or tag in its types.xml entry to move it.
    </p>
  );
}
